import { ComponentWithTooltip } from 'components/ComponentWithTooltip';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import {
    ControlledChoice,
    ControlledMultiselectField,
    ControlledRadio,
    ControlledSelectField,
    ControlledTextField,
} from 'components/Form';
import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogOtherCostVariableCreateInput, CatalogOtherCostVariableUpdateInput } from 'gql/graphql';
import { CatalogOtherCostVariableTypeEnum, ProjectMethodologyEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useGroupList } from 'pages/Catalogs/Common/CatalogVariableGroups/hooks';
import { getOtherFormulaSourceObjectGql } from 'pages/Project/Common/Contract/contractQueries';
import type { AnyObject } from 'types/AnyObject';
import { cnMixSpace, Grid, GridItem, IconQuestion } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useForm, useWatch } from 'react-hook-form';

import { useGetLimitedTypesData } from '../CatalogLimitedType/hooks/useGetLimitedTypesData';
import { useGetTypesData } from '../CatalogOthersType/hooks';
import type {
    FieldValuesCreateOrEditStructure,
    TransformedValuesCreateOrEditStructure,
    VariableIdType,
} from '../types';

import {
    DefaultFromValues,
    LIMITED_COST_TYPE_PLACEHOLDER,
    LIMITED_COST_TYPE_TOOLTIP,
    LinkTypeRadioItems,
    OTHER_COST_TYPE_PLACEHOLDER,
    OTHER_COST_TYPE_TOOLTIP,
    TypeItems,
} from './constants';
import {
    useCreateOtherVariable,
    useDataSources,
    useIsUniqueVariableName,
    useOtherVariable,
    useUpdateOtherVariable,
} from './hooks';
import { getVariableTypeChoiceLabel } from './utils';

import Styles from './createOrEditVariable.module.css';

const isInputForUpdateVariable = (
    input: CatalogOtherCostVariableCreateInput | CatalogOtherCostVariableUpdateInput,
): input is CatalogOtherCostVariableUpdateInput => 'id' in input;

interface ICreateOrEditOtherVariableProps {
    id: Exclude<VariableIdType, undefined>;
    catalogUniqueId: string;
    onClose: () => void;
    updateCatalogVariables: () => void;
    methodology: ProjectMethodologyEnum;
    isGlobal?: boolean;
}

export const CreateOrEditOtherVariable = ({
    id,
    catalogUniqueId,
    onClose,
    updateCatalogVariables,
    methodology,
    isGlobal = false,
}: ICreateOrEditOtherVariableProps) => {
    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const [isDataSourceDisabled, setIsDataSourceDisabled] = useState<boolean>(false);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const { control, handleSubmit, formState, setValue, trigger } = useForm<
        FieldValuesCreateOrEditStructure,
        AnyObject,
        TransformedValuesCreateOrEditStructure
    >({
        criteriaMode: 'all',
        shouldUnregister: true,
        defaultValues: DefaultFromValues,
    });

    const { deserializeFromBackend, serializeStringForBackend } = useEditorParseData({});

    const name = useWatch<FieldValuesCreateOrEditStructure, 'name'>({ control, name: 'name' });
    const entities = useWatch<FieldValuesCreateOrEditStructure, 'entities'>({ control, name: 'entities' });
    const type = useWatch<FieldValuesCreateOrEditStructure, 'type'>({ control, name: 'type' });
    const linkType = useWatch<FieldValuesCreateOrEditStructure, 'linkType'>({
        defaultValue: LinkTypeRadioItems[0],
        control,
        name: 'linkType',
    });

    const [isLinkType, setIsLinkType] = useState(false);
    const [costTypePlaceholder, setCostTypePlaceholder] = useState(OTHER_COST_TYPE_PLACEHOLDER);
    const [costTypeTooltipText, setCostTypeTooltipText] = useState(OTHER_COST_TYPE_TOOLTIP);

    const { variableData, variableStatus, entitiesData } = useOtherVariable(id, methodology);
    const { typesData: otherTypesData, typesStatus } = useGetTypesData({
        methodology,
        withEffect: isLinkType,
    });
    const { limitedTypesData, limitedTypesStatus } = useGetLimitedTypesData({ methodology });
    const { dataSourcesData, dataSourcesStatus } = useDataSources(type?.label, entities, methodology);
    const { groupListData } = useGroupList({ catalogUniqueId, globalOnly: isGlobal, methodology });
    const { isUniqueVariableNameData, isUniqueVariableNameStatus } = useIsUniqueVariableName({
        catalogUniqueId,
        id,
        name,
        variableType: isLinkType ? linkType?.id : (type?.label as CatalogOtherCostVariableTypeEnum),
        globalOnly: isGlobal,
        methodology,
    });
    const { createOtherVariableHandler, createOtherVariableStatus } = useCreateOtherVariable({
        closeModalHandler: onClose,
        updateCatalogVariables,
    });
    const { updateOtherVariableHandler, updateOtherVariableStatus } = useUpdateOtherVariable({
        closeModalHandler: onClose,
        updateCatalogVariables,
    });

    const isFormInitialized = useRef(false);
    const isLinkTypesInitialized = useRef(false);

    useEffect(() => {
        if (isFormInitialized.current || !variableData) {
            return;
        }

        setValue('name', variableData.name);
        setValue('group', { label: variableData.group.name, id: variableData.group.id });
        setValue('type', { label: variableData.type });

        const linkTypeFromServer = LinkTypeRadioItems.find((l) => l.id === variableData.type);

        if (linkTypeFromServer) {
            setValue('linkType', linkTypeFromServer);
        }

        if (variableData.type === CatalogOtherCostVariableTypeEnum.Simple) {
            setValue(
                'entities',
                variableData.entities.map((entityData) => ({ id: entityData.uniqueId, label: entityData.name })),
            );
        }

        if (variableData.dataSource) {
            setValue('dataSource', {
                id: variableData.dataSource.id,
                label: variableData.dataSource.name,
            });
        }

        if (variableData.formula) {
            setValue('formula', deserializeFromBackend(variableData.formula));
        }

        isFormInitialized.current = true;
    }, [deserializeFromBackend, setValue, variableData, limitedTypesData, otherTypesData]);

    useEffect(() => {
        if (!id) {
            return;
        }

        // Ждем инициализацию остальной формы.  Не выполняем, если уже проинициализировали часть с Типом ссылки
        if (!isFormInitialized.current || isLinkTypesInitialized.current) {
            return;
        }

        // Ждем инициализацию нужных данных
        if (!variableData || !limitedTypesData?.length || !otherTypesData?.length) {
            return;
        }

        const currentTypeById =
            type?.label === CatalogOtherCostVariableTypeEnum.LimitedCostType
                ? limitedTypesData.find((t) => t.id === variableData.limitedCostTypeId)
                : otherTypesData.find((t) => t.id === variableData.otherCostTypeId);

        setValue('costTypeId', currentTypeById);

        isLinkTypesInitialized.current = true;
    }, [limitedTypesData, otherTypesData, setValue, type, id, variableData]);

    useEffect(() => {
        setValue('costTypeId', undefined);
    }, [linkType, setValue]);

    useEffect(() => {
        if (dataSourcesStatus === RequestStatus.SUCCESS) {
            setIsDataSourceDisabled(Number(entities?.length) > 1 && !dataSourcesData.length);
        }
    }, [dataSourcesData.length, dataSourcesStatus, entities?.length]);

    const title = useMemo(() => (id ? 'Редактирование переменной' : 'Создание переменной'), [id]);

    const formulaQueryVariables = useMemo(
        () => ({
            skip: 0,
            take: 1000,
            methodology,
            globalOnly: isGlobal,
        }),
        [isGlobal, methodology],
    );

    const groupItems = useMemo(
        () => groupListData.map((group) => ({ id: group.id, label: group.name })),
        [groupListData],
    );

    const entitiesItems = useMemo(
        () => entitiesData.map((entityData) => ({ id: entityData.uniqueId, label: entityData.name })),
        [entitiesData],
    );

    const dataSourcesItems = useMemo(
        () =>
            dataSourcesData.map((dataSource) => ({
                id: dataSource.id,
                label: dataSource.name,
            })),
        [dataSourcesData],
    );

    const onModalClose = useCallback(() => {
        if (formState?.isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onClose();
        }
    }, [formState?.isDirty, handleOpenConfirmationDialog, onClose]);

    const onSubmit = useCallback(
        (formData: TransformedValuesCreateOrEditStructure) => {
            const costTypeKey: keyof (CatalogOtherCostVariableCreateInput | CatalogOtherCostVariableUpdateInput) =
                formData.linkType?.id === CatalogOtherCostVariableTypeEnum.OtherCostType
                    ? 'otherCostTypeId'
                    : 'limitedCostTypeId';
            const costType = isLinkType ? { [costTypeKey]: formData.costTypeId?.id } : {};

            const input: CatalogOtherCostVariableCreateInput | CatalogOtherCostVariableUpdateInput = {
                catalogUniqueId,
                isGlobal,
                entityUniqueIds: formData.entities?.map((entity) => entity.id),
                groupId: formData.group.id,
                name: formData.name,
                type: isLinkType ? formData.linkType.id : formData.type.label,
                dataSourceId:
                    formData.type.label === CatalogOtherCostVariableTypeEnum.Simple
                        ? Number(formData.dataSource?.id)
                        : null,
                formula:
                    formData.type.label === CatalogOtherCostVariableTypeEnum.Composite
                        ? serializeStringForBackend(formData.formula ?? '')
                        : null,
                ...costType,

                ...(id ? { id } : {}),
            };

            if (isInputForUpdateVariable(input)) {
                updateOtherVariableHandler(input);

                sendGpnAnalyticsEvent({
                    componentId: 'rim_edit directory window save button_pressed',
                });
            } else {
                createOtherVariableHandler(input);
            }
        },
        [
            catalogUniqueId,
            createOtherVariableHandler,
            id,
            isGlobal,
            sendGpnAnalyticsEvent,
            serializeStringForBackend,
            updateOtherVariableHandler,
            isLinkType,
        ],
    );

    const nameRules = useMemo<Pick<RegisterOptions<FieldValuesCreateOrEditStructure, 'name'>, 'validate'>>(
        () => ({
            validate: (value) => {
                if (!value) {
                    return requiredBaseRule.required;
                }

                if (value.length > 255) {
                    return 'Не более 255 символов';
                }

                if (
                    value === name &&
                    isUniqueVariableNameStatus === RequestStatus.SUCCESS &&
                    !isUniqueVariableNameData
                ) {
                    return 'Переменная с таким наименованием уже существует';
                }

                return true;
            },
        }),
        [isUniqueVariableNameData, isUniqueVariableNameStatus, name],
    );

    useEffect(() => {
        if (isUniqueVariableNameStatus === RequestStatus.SUCCESS) {
            trigger('name');
        }
    }, [isUniqueVariableNameStatus, trigger]);

    useEffect(() => {
        const isLimitedLinkType = linkType?.id === CatalogOtherCostVariableTypeEnum.LimitedCostType;

        setIsLinkType(
            type?.label === CatalogOtherCostVariableTypeEnum.OtherCostType ||
                type?.label === CatalogOtherCostVariableTypeEnum.LimitedCostType,
        );

        setCostTypeTooltipText(!isLimitedLinkType ? OTHER_COST_TYPE_TOOLTIP : LIMITED_COST_TYPE_TOOLTIP);
        setCostTypePlaceholder(!isLimitedLinkType ? OTHER_COST_TYPE_PLACEHOLDER : LIMITED_COST_TYPE_PLACEHOLDER);
    }, [type, linkType]);

    return (
        <ModalDialog
            isOpen
            isLoading={!!id && variableStatus === RequestStatus.FETCHING}
            isCommit={[createOtherVariableStatus, updateOtherVariableStatus].includes(RequestStatus.FETCHING)}
            title={title}
            onClose={onModalClose}
            onConfirm={handleSubmit(onSubmit)}
            btnConfirmLabel={id ? 'Сохранить' : 'Создать'}
            btnConfirmDisabled={!formState.isDirty}
            bodyWidth={600}
            withTitleCloseButton
            isOnClickOutsideDisable
        >
            <div className={Styles.modalBody}>
                <Grid rowGap="s" cols="1">
                    <GridItem>
                        <ControlledTextField
                            name="name"
                            label="Наименование переменной"
                            control={control}
                            placeholder="Введите наименование"
                            type="text"
                            required
                            tooltipSize={
                                isUniqueVariableNameStatus === RequestStatus.SUCCESS && !isUniqueVariableNameData
                                    ? 'm'
                                    : 's'
                            }
                            rules={nameRules}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledSelectField
                            name="group"
                            label={
                                (
                                    <div className={Styles.centered}>
                                        <span>Группа переменных</span>
                                        <span className="FieldLabel-Star">*</span>
                                        <ComponentWithTooltip
                                            tooltipText="Добавить новую группу можно на странице Справочника переменных для формул прочих затрат"
                                            className={`${cnMixSpace({ mL: '2xs' })} ${Styles.centered}`}
                                        >
                                            <IconQuestion view="secondary" />
                                        </ComponentWithTooltip>
                                    </div>
                                ) as any
                            }
                            control={control}
                            placeholder="Выберите группу"
                            items={groupItems}
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    <GridItem>
                        <ControlledChoice
                            control={control}
                            items={TypeItems}
                            name="type"
                            getItemLabel={getVariableTypeChoiceLabel}
                            textLabel="Тип переменной"
                            textLabelClassName={Styles.typeChoice}
                            required
                            rules={requiredBaseRule}
                        />
                    </GridItem>
                    {type && type?.label === CatalogOtherCostVariableTypeEnum.Simple && (
                        <>
                            <GridItem>
                                <ControlledMultiselectField
                                    name="entities"
                                    placeholder="Выберите сущность"
                                    label="Сущность для переменной"
                                    control={control}
                                    labelPosition="left"
                                    items={entitiesItems}
                                    required
                                    rules={requiredBaseRule}
                                />
                            </GridItem>
                            <GridItem>
                                <ControlledSelectField
                                    name="dataSource"
                                    label="Источник данных"
                                    control={control}
                                    placeholder={
                                        isDataSourceDisabled ? 'Нет общих источников' : 'Выберите источник данных'
                                    }
                                    items={dataSourcesItems}
                                    disabled={isDataSourceDisabled}
                                    required
                                    rules={requiredBaseRule}
                                    clearOnDisabled
                                />
                            </GridItem>
                        </>
                    )}
                    {type && type?.label === CatalogOtherCostVariableTypeEnum?.Composite && (
                        <FormulaEditor
                            name="formula"
                            control={control}
                            getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                            queryVariables={formulaQueryVariables}
                            tooltipTitle="Формула расчета"
                            tooltipTitleClassName={Styles.formulaTitle}
                            wrapperClassName={Styles.formulaWrapper}
                            isRequired
                            rules={requiredBaseRule}
                            methodology={methodology}
                        />
                    )}
                    {isLinkType && (
                        <>
                            {methodology === ProjectMethodologyEnum.Uer && (
                                <GridItem className={`${Styles.centered} ${Styles.linkTypeGridRow}`}>
                                    <ControlledRadio
                                        textLabel={
                                            <div className={`${Styles.centered} ${Styles.linkTypeLabel}`}>
                                                <span>Тип ссылки</span>
                                                <span className="FieldLabel-Star">*</span>
                                            </div>
                                        }
                                        textView="secondary"
                                        textSize="s"
                                        direction="row"
                                        name="linkType"
                                        control={control}
                                        items={LinkTypeRadioItems}
                                    />
                                </GridItem>
                            )}
                            <GridItem>
                                <ControlledSelectField
                                    name="costTypeId"
                                    label={
                                        (
                                            <div className={Styles.centered}>
                                                <span>{costTypePlaceholder}</span>
                                                <span className="FieldLabel-Star">*</span>
                                                <ComponentWithTooltip
                                                    tooltipText={costTypeTooltipText}
                                                    className={`${cnMixSpace({ mL: '2xs' })} ${Styles.centered}`}
                                                >
                                                    <IconQuestion view="secondary" />
                                                </ComponentWithTooltip>
                                            </div>
                                        ) as any
                                    }
                                    placeholder={costTypePlaceholder}
                                    control={control}
                                    labelPosition="left"
                                    getItemLabel={(t) => t.name}
                                    items={
                                        linkType?.id === CatalogOtherCostVariableTypeEnum.OtherCostType
                                            ? otherTypesData
                                            : limitedTypesData
                                    }
                                    rules={requiredBaseRule}
                                    isLoading={
                                        typesStatus === RequestStatus.FETCHING ||
                                        limitedTypesStatus === RequestStatus.FETCHING
                                    }
                                />
                            </GridItem>
                        </>
                    )}
                </Grid>
            </div>
            <ConfirmationDialog
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Введенные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
            />
        </ModalDialog>
    );
};

CreateOrEditOtherVariable.displayName = 'CreateOrEditOtherVariable';
